import { useTranslation } from 'react-i18next'
import { RiAddLine, RiArrowDownLine, RiArrowUpLine, RiDeleteBinLine } from '@remixicon/react'
import {
  showIfError,
  SURVEY_QUESTION_TYPES,
  type QuestionConfig,
  type ShowIf,
  type SurveyQuestionInput,
  type SurveyQuestionType,
} from 'shared'
import { Alert, Button, Card, Field, Input, Select, Textarea } from '../ui'
import { cx, focusRing } from '../../lib/utils'
import ConditionBuilder from './ConditionBuilder'
import TableBuilder from './TableBuilder'
import { defaultTableConfig } from './tableAnswers'

/** A config with its rule set or taken off, and no empty config left behind. */
function withShowIf(config: QuestionConfig | null | undefined, showIf: ShowIf | undefined) {
  const next: QuestionConfig = { ...config }
  if (showIf) next.showIf = showIf
  else delete next.showIf
  return Object.keys(next).length > 0 ? next : null
}

export default function QuestionBuilder({
  questions,
  onChange,
  excludeFile = false,
}: {
  questions: SurveyQuestionInput[]
  onChange: (questions: SurveyQuestionInput[]) => void
  /** An anonymous survey cannot ask for a file: it is stored with whoever uploaded it. */
  excludeFile?: boolean
}) {
  const { t } = useTranslation()

  const update = (index: number, patch: Partial<SurveyQuestionInput>) =>
    onChange(questions.map((question, i) => (i === index ? { ...question, ...patch } : question)))

  // A code is what other questions' rules point at, so renaming one carries the
  // rules with it; otherwise a rename would quietly break every rule that used it.
  const setCode = (index: number, code: string | null) => {
    const before = questions[index].code
    onChange(
      questions.map((question, i) => {
        if (i === index) return { ...question, code }
        const showIf = question.config?.showIf
        if (!before || !code || !showIf?.conditions.some((rule) => rule.question === before)) {
          return question
        }
        return {
          ...question,
          config: {
            ...question.config,
            showIf: {
              ...showIf,
              conditions: showIf.conditions.map((rule) =>
                rule.question === before ? { ...rule, question: code } : rule,
              ),
            },
          },
        }
      }),
    )
  }

  const problem = showIfError(
    questions.map((question) => ({
      type: question.type,
      code: question.code || null,
      config: question.config,
    })),
  )

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= questions.length) return
    const next = [...questions]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
        {t('surveys.questions')}
      </h3>

      {problem && (
        <Alert variant="warning" className="mt-4">
          {t('surveys.condition.invalid', { problem })}
        </Alert>
      )}

      <ul className="mt-4 space-y-4">
        {questions.map((question, index) => (
          <li
            key={index}
            className="rounded-md border border-gray-200 p-4 dark:border-gray-800"
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {index + 1}
              </span>
              <div className="flex gap-1">
                <IconButton
                  label={t('surveys.moveUp')}
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                >
                  <RiArrowUpLine className="size-4" aria-hidden="true" />
                </IconButton>
                <IconButton
                  label={t('surveys.moveDown')}
                  onClick={() => move(index, 1)}
                  disabled={index === questions.length - 1}
                >
                  <RiArrowDownLine className="size-4" aria-hidden="true" />
                </IconButton>
                <IconButton
                  label={t('common.delete')}
                  danger
                  onClick={() => onChange(questions.filter((_, i) => i !== index))}
                >
                  <RiDeleteBinLine className="size-4" aria-hidden="true" />
                </IconButton>
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-[1fr_11rem]">
                <Field
                  label={t(question.type === 'section' ? 'surveys.sectionTitle' : 'surveys.fields.label')}
                  required
                >
                  <Input
                    value={question.label}
                    onChange={(event) => update(index, { label: event.target.value })}
                  />
                </Field>
                <Field label={t('surveys.fields.code')} hint={t('surveys.fields.codeHint')}>
                  <Input
                    value={question.code ?? ''}
                    maxLength={40}
                    spellCheck={false}
                    autoCapitalize="none"
                    className="font-mono"
                    onChange={(event) =>
                      // Kept to what the server accepts as it is typed, so a code is never
                      // refused at the end for a capital letter.
                      setCode(index, event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') || null)
                    }
                  />
                </Field>
              </div>

              <Field label={t('surveys.fields.description')}>
                <Textarea
                  rows={2}
                  value={question.description ?? ''}
                  onChange={(event) => update(index, { description: event.target.value || null })}
                />
              </Field>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('surveys.fields.type')}>
                  <Select
                    value={question.type}
                    onChange={(event) => {
                      const type = event.target.value as SurveyQuestionType
                      // Each type keeps its own settings: a table's columns mean nothing
                      // to a text question, and a table needs some to be a table at all.
                      const showIf = question.config?.showIf
                      const base =
                        type === 'table'
                          ? defaultTableConfig(t('surveys.table.defaultColumn'))
                          : question.type === 'table'
                            ? null
                            : question.config
                      update(index, {
                        type,
                        // A heading is never required; its rule, if it has one, stays.
                        ...(type === 'section' ? { required: false } : {}),
                        config: withShowIf(type === 'section' ? null : base, showIf),
                      })
                    }}
                    options={SURVEY_QUESTION_TYPES.filter(
                      (type) => !excludeFile || type !== 'file',
                    ).map((type) => ({
                      value: type,
                      label: t(`surveys.type_${type}`),
                    }))}
                  />
                </Field>

                {question.type !== 'section' && (
                  <Field label={t('surveys.fields.required')}>
                    <Select
                      value={question.required ? 'yes' : 'no'}
                      onChange={(event) => update(index, { required: event.target.value === 'yes' })}
                      options={[
                        { value: 'no', label: t('common.no') },
                        { value: 'yes', label: t('common.yes') },
                      ]}
                    />
                  </Field>
                )}
              </div>

              <ConditionBuilder
                index={index}
                questions={questions}
                onChange={(showIf) => update(index, { config: withShowIf(question.config, showIf) })}
              />

              {question.type === 'table' && (
                <TableBuilder
                  config={question.config ?? {}}
                  onChange={(config) => update(index, { config })}
                />
              )}

              {(question.type === 'single_choice' || question.type === 'multi_choice') && (
                <Field label={t('surveys.fields.options')} hint={t('surveys.fields.optionsHint')}>
                  <Textarea
                    rows={3}
                    value={(question.config?.options ?? []).map((option) => option.label).join('\n')}
                    onChange={(event) =>
                      update(index, {
                        config: {
                          ...question.config,
                          options: event.target.value
                            .split('\n')
                            .map((line) => line.trim())
                            .filter(Boolean)
                            .map((line) => ({ value: line, label: line })),
                        },
                      })
                    }
                  />
                </Field>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Button
        variant="secondary"
        className="mt-4"
        onClick={() => onChange([...questions, { type: 'short_text', label: '' }])}
      >
        <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
        {t('surveys.addQuestion')}
      </Button>
    </Card>
  )
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'rounded-md p-1.5 text-gray-400 transition disabled:opacity-30',
        danger
          ? 'hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10'
          : 'hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800',
        focusRing,
      )}
    >
      {children}
    </button>
  )
}
